import './Date';
import { extend as extendDate } from './Date';
import './Array';
import { extend as extendArray } from './Array';
import './Object';
import { extend as extendObject } from './Object';
// Force implementation
extendDate();
extendArray();
extendObject();

declare global {
  // IPhone structure
  interface IPhone {
    prefix: Number | number | null;
    number: Number | number | null;
  }

  // IRole structure
  interface IRole {
    name: String | string;
    permissions: Array<String | string>;
  }

  // IAPI structure
  interface IAPI {
    status: boolean;
    name: String | string | null;
    surname: String | string | null;
    permissions: Array<String | string> | null;
  }

  // IUser structure
  interface IUser {
    id: Number | number | String | string | null;
    name: String | string | null;
    surname: String | string | null;
    phone: IPhone;
    mobile: IPhone;
    email: String | string | null;
    created_at: Date | String | string | null;
    updated_at: Date | String | string | null;
    deleted_at: Date | String | string | null;
    password?: String | string | null | undefined;
    signatureCode?: Number | number | null | undefined;
    remember?: boolean | undefined;
    roles: Array<IRole>;
    teams: Array<String | string>;
    notification_subscriptions: Array<String | string>;
    organization?: Object | null | undefined;
    api?: IAPI | null | undefined;
  }
  // IEUser omitted roles...
  type IEUser = Omit<IUser, 'roles'>;
  // IAuth structure
  interface IAuth extends IEUser {
    hasPermission: (...permissions: Array<String | string>) => boolean;
    hasApiPermission: (...permissions: Array<String | string>) => boolean;
    hasRole: (...roles: Array<String | string>) => boolean;
    routeAvailable: (to: { name: String | string }, api: boolean) => boolean;
    teams: Array<String | string>;
    roles: Array<String | string>;
    permissions: Array<String | string>;
    organization: Object | null | undefined;
    notification_subscriptions: Array<String | string>;
    api: IAPI | null;
  }

  // class Auth implements IAuth {
  //   id: Number | String | string | null;
  //   name: String | string | null;
  //   surname: String | string | null;
  //   phone: IPhone;
  //   mobile: IPhone;
  //   email: String | string | null;
  //   created_at: Date | String | string | null;
  //   updated_at: Date | String | string | null;
  //   deleted_at: Date | String | string | null;
  //   roles: Array<String>;
  //   permissions: Array<String>;
  //   organization: Object | null | undefined;
  //   notification_subscriptions: Array<String | string>;
  //   api: IAPI | null;
  //   // Instance check bcs. typescript
  //   protected static instanceOfUser(object: any): object is IUser;
  //   // Init
  //   protected init(object?: IUser | null): void;
  //   // C'tor
  //   public constructor(object?: IUser | null);
  //   // Permission check method
  //   public permissionCheck(localPermissions: Array<String | string>, ...permissions: Array<String | string>): boolean
  //   // Has permission in class attr.
  //   public hasPermission(...permissions: Array<String | string>): boolean;
  //   // Has API permission in class attr.
  //   public hasApiPermission(...permissions: Array<String | string>): boolean;
  //   // Role check method
  //   public hasRole(...roles: Array<String | string>): boolean;
  //   // Permission check method
  //   public routeAvailable(to: { name: String | string }, api: boolean): boolean;
  // }
};

export class Auth implements IAuth {
  id: Number | number | String | string | null = null;
  name: String | string | null = null;
  surname: String | string | null = null;
  phone = <IPhone>{
    prefix: null,
    number: null,
  };
  mobile = <IPhone>{
    prefix: null,
    number: null,
  };
  email: String | string | null = null;
  created_at: Date | String | string | null = null;
  updated_at: Date | String | string | null = null;
  deleted_at: Date | String | string | null = null;
  teams: Array<String | string> = [];
  roles: Array<String | string> = [];
  permissions: Array<String | string> = [];
  notification_subscriptions: Array<String | string> = [];
  organization: Object | null | undefined = null;
  api: IAPI | null = null;

  // Instance check bcs. typescript
  protected static instanceOfUser(object: any): object is IUser {
    if (object)
      return (
        ('id' in object) &&
        ('name' in object) &&
        ('surname' in object) &&
        ('phone' in object) &&
        ('mobile' in object) &&
        ('email' in object) &&
        ('roles' in object) &&
        ('created_at' in object) &&
        ('updated_at' in object) &&
        ('deleted_at' in object)
      );

    return false;
  };

  // Init
  protected init(
    object?: IUser | null
  ): void {
    if (Auth.instanceOfUser(object)) {
      this.id = object.id;
      this.name = object.name;
      this.surname = object.surname;
      this.phone = <IPhone>{
        prefix: object.phone?.prefix,
        number: object.phone?.number
      };
      this.mobile = <IPhone>{
        prefix: object.mobile?.prefix,
        number: object.mobile?.number
      };
      this.email = object.email;
      this.created_at = Date.parseData(object.created_at);
      this.updated_at = Date.parseData(object.updated_at);
      this.deleted_at = Date.parseData(object.deleted_at);
      this.roles = [];
      this.permissions = [];
      if (Array.isArray(object.roles)) {
        this.roles = object.roles.pluck('name');
        object.roles?.forEach((role: IRole) => (Array.isArray(role?.permissions) ? (this.permissions = this.permissions.union(role.permissions)) : null));
      }
      this.permissions = this.permissions.unique();
      this.api = (object.api ?? null);
      this.organization = (object.organization ?? null);
      this.notification_subscriptions = object.notification_subscriptions;
      this.teams = object.teams;
    }
  };

  // C'tor
  public constructor(
    object?: IUser | null
  ) {
    this.init(object);
  };

  // Permissions check method
  public permissionCheck(
    localPermissions: Array<String | string>,
    ...permissions: Array<String | string>
  ): boolean {
    if (Array.isArray(permissions) && Array.isArray(localPermissions)) {
      permissions = (permissions?.map((permission: String | string) => permission?.replace(/[.+^${}()|[\]\\]/g, '\\$&')?.replace(/\*/g, '.*')) ?? []);
      const pattern = new RegExp(`^(?:${permissions?.join('|')})$`);
      return localPermissions?.some((permission: String | string) => pattern.test((permission as string)));
    }

    return false;
  };

  // Has permission in class attr.
  public hasPermission(
    ...permissions: Array<String | string>
  ): boolean {
    return this.permissionCheck(this.permissions, ...permissions);
  };

  // Has API permission in class attr.
  public hasApiPermission(
    ...permissions: Array<String | string>
  ): boolean {
    if (this.api?.status ? Array.isArray(this.api?.permissions) : false)
      return this.permissionCheck(this.api?.permissions as Array<String | string>, ...permissions);

    return false;
  };

  // Team check method
  public hasTeam(
    ...teams: Array<String | string>
  ): boolean {
    if (Array.isArray(teams) && Array.isArray(this.teams)) {
      teams = (teams?.map((team: String | string) => team?.replace(/[.+^${}()|[\]\\]/g, '\\$&')?.replace(/\*/g, '.*')) ?? []);
      const pattern = new RegExp(`^(?:${teams?.join('|')})$`);
      return this.teams?.some((team: String | string) => pattern.test((team as string)));
    }

    return false;
  };

  // Role check method
  public hasRole(
    ...roles: Array<String | string>
  ): boolean {
    if (Array.isArray(roles) && Array.isArray(this.roles)) {
      roles = (roles?.map((role: String | string) => role?.replace(/[.+^${}()|[\]\\]/g, '\\$&')?.replace(/\*/g, '.*')) ?? []);
      const pattern = new RegExp(`^(?:${roles?.join('|')})$`);
      return this.roles?.some((role: String | string) => pattern.test((role as string)));
    }

    return false;
  };

  // Permission check method
  public routeAvailable(
    to: { name: String | string },
    api: boolean = false
  ): boolean {
    let routePermission = to.name?.split('-') as Array<String | string>;
    switch (routePermission[routePermission.length - 1]) {
      case 'archive':
        routePermission[routePermission.length - 1] = 'archive';
        break;
      case 'delete':
        routePermission[routePermission.length - 1] = 'delete';
        break;
      case 'edit':
      case 'active':
        routePermission[routePermission.length - 1] = 'update';
        break;
      case 'clone':
      case 'create':
        routePermission[routePermission.length - 1] = 'create';
        break;
      default:
        routePermission.push('read');
        break;
    }
    routePermission = routePermission.filter((n: String | string) => (n !== 'id'));
    // Permission direct exist
    if (api) {
      if (this.hasApiPermission(routePermission.join('-')))
        return true;
    } else if (this.hasPermission(routePermission.join('-')))
      return true;
    // Permissions cascading exist
    if (Array.isArray(this.permissions)) {
      let permissionsCascade = [] as Array<String | string>;
      switch (routePermission.pop()) {
        case 'archive':
        case 'active':
        case 'read':
          permissionsCascade.push(routePermission.join('-') + '-read');
        case 'clone':
        case 'create':
          permissionsCascade.push(routePermission.join('-') + '-create');
        case 'update':
          permissionsCascade.push(routePermission.join('-') + '-update');
        case 'delete':
          permissionsCascade.push(routePermission.join('-') + '-delete');
      }
      if (api) {
        if (this.hasApiPermission(...permissionsCascade))
          return true;
      } else if (this.hasPermission(...permissionsCascade))
        return true;
    }

    return false;
  };
};

export {}; // Keeps it a valid module
